import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query/react';
import { Mutex } from 'async-mutex';
import { REHYDRATE } from 'redux-persist';
import constants from 'utils/constants';
import type { RootState } from 'store/index';
import { logout } from 'store/auth/auth-slice';
import { enqueueSnackbar } from 'notistack';
import { t } from 'i18next';

// mutex is used to prevent multiple unauthorized errors
const mutex = new Mutex();

const baseQuery = fetchBaseQuery({
  baseUrl: constants.siteUrl + '/base',
  credentials: 'include',
  prepareHeaders: (headers, { getState }) => {
    const languageId = (getState() as RootState).app.languageId?.toString();
    const sessionId = (getState() as RootState).auth?.user?.sessionId;

    // set xsrf token to request header from cookie
    const xsrfToken = document.cookie
      .split(';')
      .find((row) => row.trim().startsWith('XSRF-TOKEN='))
      ?.split('=')[1];

    // @ts-expect-error api model error
    // decode xsrf token
    const decodeToken = decodeURIComponent(xsrfToken);

    if (xsrfToken) {
      headers.set('X-XSRF-TOKEN', decodeToken);
    }

    headers.set('sessionId', sessionId ?? '');
    headers.set('languageId', languageId);
    headers.set('applicationCode', 'APP02');

    return headers;
  },
});

const baseQueryWithReauth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions: { isProfile?: boolean; isShowErrorMessage?: boolean; isShowSuccessMessage?: boolean }
) => {
  // wait until the mutex is available without locking it
  await mutex.waitForUnlock();

  // extra options for endpoints that require profile object
  const newArgs = args;
  const isTypeOfArgsValid = typeof newArgs === 'string' ? false : true;
  const isProfile = extraOptions?.isProfile ? true : false;
  const isShowSuccessMessage = extraOptions?.isShowSuccessMessage ? true : false;
  const isShowErrorMessage = extraOptions?.isShowErrorMessage === false ? false : true;

  // @ts-expect-error api model error
  // makes url kebab case for masking api
  newArgs.url = ToKebabCase(newArgs.url);

  if (isTypeOfArgsValid && isProfile) {
    const profile = (api.getState() as RootState).auth.user;

    const isProfileSentInBody = Boolean((newArgs as FetchArgs).body?.profile); // for overwrite
    let newBody;

    if (isProfileSentInBody) {
      newBody = (newArgs as FetchArgs).body;
    } else {
      newBody = (newArgs as FetchArgs).body ? { ...(newArgs as FetchArgs).body, profile } : { profile };
    }

    (newArgs as FetchArgs).body = newBody;
  }

  let result = await baseQuery(newArgs, api, extraOptions);

  if (result.error?.status === 401) {
    // @ts-expect-error api model error
    if (result.error?.data?.exception?.type === 4) {
      // when logged in from another device
      api.dispatch(logout());
    } else {
      // wait until the mutex is available without locking it
      await mutex.waitForUnlock();
      result = await baseQuery(newArgs, api, extraOptions);

      // @ts-expect-error api model error
      if (result.error?.status === 401 && result.error?.data?.exception?.type === 4) {
        // when logged in from another device after getting tokens
        api.dispatch(logout());
      }
    }
  }

  // @ts-expect-error api model error
  if ((result.error || result.data?.exception?.message) && isShowErrorMessage) {
    // prettier-ignore
    // @ts-expect-error api model error
    enqueueSnackbar(result.data?.exception?.message || result.error?.data?.exception?.message ||
        t('error.unexpected'), { variant: 'error', preventDuplicate: true });
  } else if (result.data && isShowSuccessMessage) {
    enqueueSnackbar(t('common:successful'), { variant: 'success' });
  }

  return result;
};

export const api = createApi({
  baseQuery: baseQueryWithReauth,
  extractRehydrationInfo(action, { reducerPath }) {
    if (action.type === REHYDRATE) {
      return action.payload?.[reducerPath];
    }
  },
  endpoints: (builder) => ({
    getToken: builder.query<ResultModel<TokenModel>, TokenRequest | null>({
      query: (body) => ({
        url: constants.tokenUrl,
        method: 'POST',
        body: body || { token: '', expire_date: '' },
      }),
    }),
  }),
});

function ToKebabCase(full_path: any) {
  const all_parts = full_path.split('?');
  const path = all_parts[0];
  const query_string = all_parts.length > 1 ? '?' + all_parts[1] : '';
  const parts = path.startsWith('/') ? path.substr(1).split('/') : path.split('/');
  const new_parts = [];
  new_parts.push(parts[0]);
  for (let i = 1; i < parts.length; i++) {
    new_parts.push(parts[i].replace(/([a-z0–9])([A-Z])/g, '$1-$2').toLowerCase());
  }
  return new_parts.join('/') + (path.includes('.json') ? '' : '.json') + query_string;
}

